import React, { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { CheckCircle, AlertCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/config";
import { useAuth } from "@/hooks/use-auth";
import { extractSafeToken } from "@/utils/security";

export default function VerifyEmailPage() {
  const [location, navigate] = useLocation();
  const [match] = useRoute("/verify-email");
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const { refreshSession } = useAuth();

  useEffect(() => {
    const verifyEmail = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = extractSafeToken(urlParams, "token");

      if (!token) {
        setStatus("error");
        setMessage("Invalid or unsafe verification link. Please check your email and try again.");
        return;
      }

      try {
        const response = await fetch(getApiUrl(`api/verify-email?token=${token}`), {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          setStatus("success");
          setMessage(data.message || "Email verified successfully!");
          
          // Refresh user data to update authentication state
          await refreshSession();
          
          // Redirect to home page after 3 seconds
          setTimeout(() => {
            navigate("/");
          }, 3000);
        } else {
          setStatus("error");
          setMessage(data.message || "Email verification failed. Please try again.");
        }
      } catch (error) {
        console.error("Email verification error:", error);
        setStatus("error");
        setMessage("Network error. Please check your connection and try again.");
      }
    };

    verifyEmail();
  }, []);
  console.log('status', status);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Email Verification
          </h1>
        </div>

        {status === "loading" && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Verifying your email address...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-green-600 dark:text-green-400">
                Verification Successful!
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {message}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You will be redirected to the homepage in a few seconds...
              </p>
            </div>
            <Button 
              onClick={() => navigate("/")}
              className="w-full"
            >
              Go to Homepage
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <AlertCircle className="w-16 h-16 text-red-500" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">
                Verification Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {message}
              </p>
            </div>
            <div className="space-y-2">
              <Button 
                onClick={() => navigate("/login")}
                className="w-full"
              >
                Go to Login
              </Button>
              <Button 
                onClick={() => navigate("/signup")}
                variant="outline"
                className="w-full"
              >
                Create New Account
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}